import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import './style.css';
import LandingPage from './components/LandingPage.js';
import Footer from './components/Footer.js';
import TopNav from './components/TopNav.js';
import { warmDiscuss } from './components/discuss/warm';
import { usePageViews } from './components/pageviews';
import { DRAFT, PROGRAMS, PROGRAM_TABS, shown, currentPath } from './components/programs';

const loaders = [];
const RELOADED = 'psm-chunk-reload';

// A page left open across a deploy asks for code files the deploy removed. Opening a page then
// reloads once, onto the new site; a second failure within a minute is left to show as an
// error rather than reload forever. The background preload never reloads: a reader mid-page
// would lose their place.
function reloadForNewSite(err) {
  if (!/ChunkLoadError|Loading (CSS )?chunk/.test(`${err && err.name} ${err && err.message}`)) return false;
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOADED) || 0) < 60000) return false;
    sessionStorage.setItem(RELOADED, String(Date.now()));
  } catch (e) {
    return false;
  }
  window.location.reload();
  return true;
}

// React.lazy over a loader that is also kept for preloadPages. The import is memoized so the
// preload and the route share one promise.
function page(load) {
  let promise = null;
  const once = () => {
    if (!promise) {
      promise = load().catch((err) => {
        promise = null;
        throw err;
      });
    }
    return promise;
  };
  loaders.push(once);
  return lazy(() => once().catch((err) => {
    if (reloadForNewSite(err)) return new Promise(() => {});
    throw err;
  }));
}

// Waits for the browser to be idle after the first page has loaded, then fetches every page
// chunk. A failed fetch is forgotten, so a click retries it.
function preloadPages() {
  const run = () => loaders.forEach((load) => load().catch(() => {}));
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
  const start = () => idle(run, { timeout: 4000 });
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

const Questions = page(() => import('./components/Questions'));
const QuestionSections = page(() => import('./components/questions/SectionsPage'));
const QuestionUpload = page(() => import('./components/questions/UploadPage'));
const Nav = page(() => import('./components/Nav'));
const NIFEEPsLimits = page(() => import('./components/NIFEEPsLimits.js'));
const NIFEBriefs = page(() => import('./components/NIFEBriefs.js'));
const NIFEDiscuss = page(() => import('./components/NIFEDiscuss.js'));
const Docs = page(() => import('./components/Docs.js'));
const TW4About = page(() => import('./components/TW4About.js'));
const TW4EPsLimits = page(() => import('./components/TW4EPsLimits.js'));
const T44CEPsLimits = page(() => import('./components/T44CEPsLimits.js'));
const T44CAbout = page(() => import('./components/T44CAbout.js'));
const T44CDiscuss = page(() => import('./components/T44CDiscuss.js'));
const T44CBriefs = page(() => import('./components/T44CBriefs.js'));
const T44CDocs = page(() => import('./components/T44CDocs.js'));
const T54ADiscuss = page(() => import('./components/T54ADiscuss.js'));
const T54AAbout = page(() => import('./components/T54AAbout.js'));
const T54AEPsLimits = page(() => import('./components/T54AEPsLimits.js'));
const T54ABriefs = page(() => import('./components/T54ABriefs.js'));
const T54ADocs = page(() => import('./components/T54ADocs.js'));
const TH73AAbout = page(() => import('./components/TH73AAbout.js'));
const TH73AEPsLimits = page(() => import('./components/TH73AEPsLimits.js'));
const TH73ABriefs = page(() => import('./components/TH73ABriefs.js'));
const TH73ADiscuss = page(() => import('./components/TH73ADiscuss.js'));
const BriefsPage = page(() => import('./components/briefs/BriefsPage'));
const NIFEAbout = page(() => import('./components/NIFEAbout.js'));
const CourseRules = page(() => import('./components/TW4CourseRules.js'));
const PracticeFlight = page(() => import('./components/practice/PracticeFlight.js'));
const Systems = page(() => import('./components/systems/Systems.js'));
const Discuss = page(() => import('./components/discuss/Discuss.js'));
const TW4JetLog = page(() => import('./components/TW4JetLog.js'));
const TW4Docs = page(() => import('./components/TW4Docs.js'));

// Every page but the landing page is its own chunk, so opening one downloads that page and not
// the other sixteen: the entry bundle used to carry Leaflet, the course-rules map, the jet log
// and all six systems diagrams to someone who asked for a discussion item.
//
// But a chunk fetched only on click makes every first click wait on the network, which is worse
// than the slow first load it replaced. So once the first page is up, the rest are downloaded in
// the background (preloadPages), and a click finds its page already here.
//
// A Discussion Items deep link also starts its mirror reads here, before its chunk arrives.
// Only on the first load: the reads are claimed by the first matching fetch, and on a later
// navigation the tab may already hold what they would return and never claim them.
warmDiscuss(window.location.pathname);

// Whether a route is served here. A draft program (`draft`), a draft tab (PROGRAM_TABS
// `draft`) and a draft Discussion Items or Briefs tab (`discuss: 'draft'`, `briefs: 'draft'`)
// are hidden from the navigation on the live site, and their addresses must not answer
// either, or the page is live to anyone who has the link. On a dev server everything is.
const under = (path, base) => path === base || path.startsWith(`${base}/`);
// The program flags that each govern one tab, named for its address.
const TAB_FLAGS = ['discuss', 'briefs'];
function routed(path) {
  const program = PROGRAMS.find((p) => under(path, p.base));
  if (!program) return true;
  if (program.draft && !DRAFT) return false;
  const tab = (PROGRAM_TABS[program.id] || []).find((t) => under(path, t.to));
  if (!tab) return true;
  if (tab.draft && !DRAFT) return false;
  const section = tab.to.slice(program.base.length + 1);
  return !TAB_FLAGS.includes(section) || shown(program[section]);
}

// The routes in a fragment that are served here.
const served = (routes) =>
  React.Children.toArray(routes.props.children).filter((route) => routed(route.props.path));

// /nife/flight/told is now on the Briefs/TOLD page; its other tabs are on EPs/Limits.
function FlightTabRedirect() {
  const { tab } = useParams();
  return <Navigate to={tab === 'told' ? '/nife/briefs/told' : `/nife/eps-limits/${tab}`} replace />;
}

// Primary was at /tw4 until 2026-10-02. Its addresses are in the wild, and pages and syllabi on
// the server still link to some, so every one of them lands on the same page under /primary.
function LegacyPrimaryRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${currentPath(pathname)}${search}${hash}`} replace />;
}

function App() {
  const location = useLocation();

  const isLanding = location.pathname === '/';

  useEffect(preloadPages, []);
  usePageViews();

  return (
    <div className="site-shell">
      {!isLanding && <TopNav />}

      <main className="site-main">
      <Suspense fallback={<div className="route-loading" />}>
      <Routes>
      {served(<>
        <Route path="/" element={<LandingPage />} />
        <Route path="/nife" element={<Navigate to="/nife/about" replace />} />
        <Route path="/nife/about" element={<NIFEAbout />} />
        <Route path="/nife/questions" element={<Questions />} />
        <Route path="/nife/questions/sections" element={<QuestionSections />} />
        <Route path="/nife/questions/upload" element={<QuestionUpload />} />
        <Route path="/nife/questions/q/:questionId" element={<Questions />} />
        <Route path="/nife/docs" element={<Docs />} />
        <Route path="/nife/nav" element={<Nav />} />
        <Route path="/nife/nav/:tab" element={<Nav />} />
        <Route path="/nife/eps-limits" element={<NIFEEPsLimits />} />
        <Route path="/nife/eps-limits/:tab" element={<NIFEEPsLimits />} />
        <Route path="/nife/briefs/*" element={<NIFEBriefs />} />
        <Route path="/nife/discuss/*" element={<NIFEDiscuss />} />
        {/* The Flight page was split into those two; its addresses are in the wild. */}
        <Route path="/nife/flight" element={<Navigate to="/nife/eps-limits" replace />} />
        <Route path="/nife/flight/:tab" element={<FlightTabRedirect />} />
        <Route path="/tw4/*" element={<LegacyPrimaryRedirect />} />
        <Route path="/primary" element={<Navigate to="/primary/about" replace />} />
        <Route path="/primary/about" element={<TW4About />} />
        <Route path="/primary/eps-limits" element={<TW4EPsLimits />} />
        <Route path="/primary/eps-limits/:tab" element={<TW4EPsLimits />} />
        <Route path="/primary/docs" element={<TW4Docs />} />
        <Route path="/primary/briefs/*" element={<BriefsPage />} />
        <Route path="/primary/courserules" element={<CourseRules />} />
        <Route path="/primary/practice/*" element={<PracticeFlight />} />
        <Route path="/primary/systems" element={<Systems />} />
        <Route path="/primary/systems/:tab" element={<Systems />} />
        <Route path="/primary/discuss/*" element={<Discuss />} />
        <Route path="/primary/jetlog" element={<TW4JetLog />} />
        <Route path="/t44c" element={<Navigate to="/t44c/about" replace />} />
        <Route path="/t44c/about" element={<T44CAbout />} />
        <Route path="/t44c/eps-limits" element={<T44CEPsLimits />} />
        <Route path="/t44c/eps-limits/:tab" element={<T44CEPsLimits />} />
        <Route path="/t44c/docs" element={<T44CDocs />} />
        <Route path="/t44c/discuss/*" element={<T44CDiscuss />} />
        <Route path="/t44c/briefs/*" element={<T44CBriefs />} />
        <Route path="/t54a" element={<Navigate to="/t54a/about" replace />} />
        <Route path="/t54a/about" element={<T54AAbout />} />
        <Route path="/t54a/eps-limits" element={<T54AEPsLimits />} />
        <Route path="/t54a/eps-limits/:tab" element={<T54AEPsLimits />} />
        <Route path="/t54a/docs" element={<T54ADocs />} />
        <Route path="/t54a/discuss/*" element={<T54ADiscuss />} />
        <Route path="/t54a/briefs/*" element={<T54ABriefs />} />
        <Route path="/th73a" element={<Navigate to="/th73a/about" replace />} />
        <Route path="/th73a/about" element={<TH73AAbout />} />
        <Route path="/th73a/eps-limits" element={<TH73AEPsLimits />} />
        <Route path="/th73a/eps-limits/:tab" element={<TH73AEPsLimits />} />
        <Route path="/th73a/briefs/*" element={<TH73ABriefs />} />
        <Route path="/th73a/discuss/*" element={<TH73ADiscuss />} />
      </>)}
      </Routes>
      </Suspense>
      </main>

      {!isLanding && <Footer />}
    </div>
  );
}

export default App;
